import { useEffect, useMemo, useState } from "react";
import { api, money, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Wallet, Truck, TrendingUp, IndianRupee, Percent } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const PER_DELIVERY = 30;  // ₹30 base per successful delivery
const COD_BONUS_RATE = 0.01; // 1% of COD collected

function dayKey(iso) { return (iso || "").slice(0, 10); }
function todayISO() { return new Date().toISOString().slice(0, 10); }
function weekStart() { const d = new Date(); const dow = d.getDay() || 7; d.setDate(d.getDate() - dow + 1); return d.toISOString().slice(0, 10); }

export default function Earnings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/assigned-to-me?limit=200")
      .then((r) => setRows((r.data.items || []).filter((o) => o.status === "delivered")))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const today = todayISO();
    const wStart = weekStart();
    const perOrder = (o) => {
      const bonus = o.payment?.method === "cod" && o.payment?.status === "captured"
        ? Number(o.pricing?.total || 0) * COD_BONUS_RATE
        : 0;
      return PER_DELIVERY + bonus;
    };
    const todayRows = rows.filter((o) => dayKey(o.delivery?.delivered_at || o.updated_at) === today);
    const weekRows  = rows.filter((o) => (dayKey(o.delivery?.delivered_at || o.updated_at) || "") >= wStart);
    const todayEarn = todayRows.reduce((s, o) => s + perOrder(o), 0);
    const weekEarn  = weekRows.reduce((s, o) => s + perOrder(o), 0);
    const total     = rows.reduce((s, o) => s + perOrder(o), 0);
    const codCollected = rows
      .filter((o) => o.payment?.method === "cod" && o.payment?.status === "captured")
      .reduce((s, o) => s + Number(o.pricing?.total || 0), 0);

    // Last 14 days
    const dayMap = new Map();
    rows.forEach((o) => {
      const k = dayKey(o.delivery?.delivered_at || o.updated_at);
      if (!k) return;
      const cur = dayMap.get(k) || { date: k, earn: 0, count: 0 };
      cur.earn += perOrder(o);
      cur.count += 1;
      dayMap.set(k, cur);
    });
    const daily = Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(-14).map((d) => ({ ...d, label: d.date.slice(5) }));

    return {
      todayEarn, weekEarn, total, codCollected,
      todayCount: todayRows.length, weekCount: weekRows.length, totalCount: rows.length,
      daily,
    };
  }, [rows]);

  return (
    <div className="p-4 space-y-4" data-testid="delivery-earnings-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Earnings</h1>
        <p className="text-xs text-muted-foreground mt-1">₹{PER_DELIVERY} per delivered order + 1% COD bonus</p>
      </div>

      {loading && <Skeleton className="h-32 w-full" />}
      {!loading && (
        <>
          <Card className="card-soft border-transparent bg-gradient-to-br from-accent to-[hsl(var(--brand-red-deep))] text-accent-foreground" data-testid="earnings-today-card">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider opacity-80">Earned today</div>
              <div className="text-4xl font-display font-semibold mt-1">{money(stats.todayEarn)}</div>
              <div className="text-sm opacity-90 mt-1">{stats.todayCount} deliveries</div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card className="card-soft border-transparent" data-testid="earnings-week-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Truck className="w-4 h-4" /></div>
                  <div className="text-xs uppercase text-muted-foreground">This week</div>
                </div>
                <div className="text-2xl font-display font-semibold">{money(stats.weekEarn)}</div>
                <div className="text-xs text-muted-foreground">{stats.weekCount} deliveries</div>
              </CardContent>
            </Card>
            <Card className="card-soft border-transparent" data-testid="earnings-total-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent grid place-items-center"><TrendingUp className="w-4 h-4" /></div>
                  <div className="text-xs uppercase text-muted-foreground">Lifetime</div>
                </div>
                <div className="text-2xl font-display font-semibold">{money(stats.total)}</div>
                <div className="text-xs text-muted-foreground">{stats.totalCount} deliveries</div>
              </CardContent>
            </Card>
          </div>

          <Card className="card-soft border-transparent" data-testid="cod-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><IndianRupee className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">Cash collected</div>
                <div className="text-xs text-muted-foreground">Deposit to Babu Store at day-end</div>
              </div>
              <div className="text-lg font-display font-semibold">{money(stats.codCollected)}</div>
            </CardContent>
          </Card>

          <Card className="card-soft border-transparent" data-testid="earnings-chart-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-secondary text-foreground grid place-items-center"><Wallet className="w-4 h-4" /></div>
                <div className="font-display text-base">Last 14 days</div>
              </div>
              <div className="h-56">
                {stats.daily.length === 0 && <div className="h-full grid place-items-center text-sm text-muted-foreground">No deliveries yet.</div>}
                {stats.daily.length > 0 && (
                  <ResponsiveContainer>
                    <LineChart data={stats.daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 30% 88%)" />
                      <XAxis dataKey="label" stroke="hsl(24 8% 40%)" fontSize={10} />
                      <YAxis stroke="hsl(24 8% 40%)" fontSize={10} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(34 30% 88%)", background: "white" }} formatter={(v) => money(v)} />
                      <Line type="monotone" dataKey="earn" stroke="hsl(var(--brand-red))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--brand-red))" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1">
            <Percent className="w-3 h-3" />
            Computed locally from your delivered orders — actual settlement follows store policy.
          </div>
        </>
      )}
    </div>
  );
}
